import type {
    AddressCountry,
    CountryRecords,
} from '@archipro-website/localisation';
import { parseAddressCountry } from '@archipro-website/localisation';

/**
 * Looks a value up by country. Returns `defaultValue` when the value names no country, or
 * when the table has no entry for it.
 *
 * A table miss is the normal case, not an error: the tables below name a country's own
 * divisions, which is editorial wording rather than something the imported place data
 * carries, so a country nobody has worded gets the generic default. Every other per-country
 * answer the address form needs comes from the `addressCountries` query.
 */
export const getValueByCountryCode = <T = string>(
    countryRecords: CountryRecords<T>,
    defaultValue: T,
    country?: string,
): T => {
    const countryCode = parseAddressCountry(country);
    if (!countryCode) {
        return defaultValue;
    }
    return countryRecords[countryCode] ?? defaultValue;
};

export const getStateNameByCountry = (country?: string): string => {
    return getValueByCountryCode(
        {
            AU: 'State',
            NZ: 'Island',
            US: 'State',
            CA: 'Province',
            // England, Scotland, Wales and Northern Ireland are the UK's top-level divisions.
            GB: 'Country',
        },
        'State',
        country,
    );
};

/**
 * What to call the field between city and postcode.
 *
 * Region or State wherever either fits, which is most places and the default for a country
 * nobody has listed. A country only earns its own word when it calls its top level neither,
 * and then it gets one word, not a compound: the field needs a label, not a taxonomy.
 */
export const getRegionNameByCountry = (country?: string): string => {
    return getValueByCountryCode(
        {
            AU: 'State',
            NZ: 'Region',
            US: 'State',
            // Only where the country calls its top level neither: Canada's are provinces (and
            // three territories, which "Province" is the closest single word for).
            CA: 'Province',
            // Not "County". What is stored for the United Kingdom is England, Scotland, Wales
            // and Northern Ireland — counties sit a level below and are not what this field
            // offers — and "Region" is a word the United Kingdom uses, so it needs no special
            // case.
            GB: 'Region',
        },
        'Region',
        country,
    );
};

/**
 * Resolves a country code or full name to an ISO code.
 * Shared with BM via `@archipro-website/localisation`'s `parseAddressCountry`.
 */
export const getCountryCodeByName = (country?: string): AddressCountry | null =>
    parseAddressCountry(country);
